import { Project, DataItem, User, NewUserInput, BulkResult } from '@/lib/constants';

const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8000';

/** Build headers, automatically including the JWT if present. */
function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { ...extra };
  const token = localStorage.getItem('token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

// ==================== Projects API ====================

// Get all projects
export async function getProjects(): Promise<Project[]> {
  const response = await fetch(`${API_BASE_URL}/getProjects`, {
    headers: authHeaders(),
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch projects: ${response.statusText}`);
  }
  
  const result = await response.json();
  return result.projects;
}

// Create a new project
export async function createProject(
  job_id: string,
  job_name: string,
  scan_count: number,
  scan_total: number,
  status: string = 'pending',
  lot_number?: string | null
) {
  const response = await fetch(`${API_BASE_URL}/createProject`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      job_id,
      job_name,
      status,
      scan_count,
      scan_total,
      lot_number,
    }),
  });
  
  if (!response.ok) {
    throw new Error(`Failed to create project: ${response.statusText}`);
  }
  
  return response.json();
}

// Update project status
export async function updateProjectState(job_id: string, status: string) {
  const response = await fetch(`${API_BASE_URL}/updateProjectState`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      job_id,
      status,
    }),
  });
  
  if (!response.ok) {
    throw new Error(`Failed to update project state: ${response.statusText}`);
  }
  
  return response.json();
}

// Delete a project
export async function deleteProject(job_id: string) {
  const response = await fetch(`${API_BASE_URL}/deleteProject?job_id=${encodeURIComponent(job_id)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  
  if (!response.ok) {
    throw new Error(`Failed to delete project: ${response.statusText}`);
  }
  
  return response.json();
}

// ==================== Data Items API ====================

// Create a data item
export async function createData(serial: string, project_id: string, stock: string) {
  const response = await fetch(`${API_BASE_URL}/createData`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      serial,
      project_id,
      stock,
    }),
  });
  
  if (!response.ok) {
    throw new Error(`Failed to create data: ${response.statusText}`);
  }
  
  return response.json();
}

// Create a project and all its data items atomically in a single transaction
export async function uploadProjectWithData(
  job_id: string,
  job_name: string,
  scan_count: number,
  scan_total: number,
  status: string = 'pending',
  lot_number: string | null,
  data: { serial: string; project_id: string; stock: string; length: string | null; mark: string | null; weight_t: number | null }[]
) {
  const response = await fetch(`${API_BASE_URL}/uploadProjectWithData`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ job_id, job_name, status, scan_count, scan_total, lot_number, data }),
  });

  if (!response.ok) {
    throw new Error(`Failed to upload project: ${response.statusText}`);
  }

  return response.json();
}

// Create multiple data items
export async function createBulkData(data: { serial: string; project_id: string; stock: string; length: string | null; mark: string | null; weight_t: number | null }[]) {
  const response = await fetch(`${API_BASE_URL}/createData`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ data }),
  });
  
  if (!response.ok) {
    throw new Error(`Failed to create bulk data: ${response.statusText}`);
  }
  
  return response.json();
}

// Get data items by project ID
export async function getDataByProjectId(project_id: string): Promise<DataItem[]> {
  const response = await fetch(`${API_BASE_URL}/getDataByProjectId?project_id=${encodeURIComponent(project_id)}`, {
    headers: authHeaders(),
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch data: ${response.statusText}`);
  }
  
  const result = await response.json();
  return result.data;
}

// Check/scan a data item — handled by the mobile app, not the web frontend.
// Function removed in Phase 2.

// Reset all scan data for a project
export async function resetProjectData(project_id: string, location: 'paint' | 'load' | 'both' = 'both') {
  const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8000';
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_BASE_URL}/resetProjectData`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ project_id, location }),
  });

  if (!response.ok) {
    throw new Error(`Failed to reset project: ${response.statusText}`);
  }

  return response.json();
}

// ==================== User Management API ====================

// List all users (admin only)
export async function listUsers(): Promise<User[]> {
  const response = await fetch(`${API_BASE_URL}/auth/users`, {
    headers: authHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch users: ${response.statusText}`);
  }

  const result = await response.json();
  return result.users;
}

// Delete a user by ID (admin only)
export async function deleteUser(userId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/auth/users/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to delete user: ${response.statusText}`);
  }
}

// Create multiple users in one request (admin only)
export async function createUsersBulk(users: NewUserInput[]): Promise<BulkResult> {
  const response = await fetch(`${API_BASE_URL}/auth/users/bulk`, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ users }),
  });

  if (!response.ok) {
    throw new Error(`Failed to create users: ${response.statusText}`);
  }

  return response.json();
}

// Archive/download a project as CSV
export async function archiveProject(job_id: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/archiveProject?job_id=${encodeURIComponent(job_id)}`, {
    headers: authHeaders(),
  });
  
  if (!response.ok) {
    throw new Error(`Failed to archive project: ${response.statusText}`);
  }
  
  // Extract filename from Content-Disposition header
  const contentDisposition = response.headers.get('content-disposition');
  let filename = `project_${job_id}.csv`; // fallback
  
  console.log('[archiveProject] Content-Disposition header:', contentDisposition);
  
  if (contentDisposition) {
    // Try to extract filename from: attachment; filename="something.csv"
    const matches = /filename[^;=\n]*=\s*"?([^";\n]+)"?/i.exec(contentDisposition);
    if (matches && matches[1]) {
      filename = matches[1];
      console.log('[archiveProject] Extracted filename:', filename);
    } else {
      console.log('[archiveProject] Could not extract filename from header');
    }
  } else {
    console.log('[archiveProject] No Content-Disposition header found');
  }
  
  console.log('[archiveProject] Final filename:', filename);
  
  // Get the CSV content and trigger download
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}
